import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, onSnapshot, writeBatch, doc, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px',
        display: 'flex',
        gap: '24px',
        height: 'calc(100vh - 100px)',
        boxSizing: 'border-box'
    },
    leftPanel: {
        flex: '2',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        background: 'var(--bg-primary)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        border: '1px solid var(--border-color)',
        overflow: 'hidden'
    },
    rightPanel: {
        flex: '1',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-primary)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        border: '1px solid var(--border-color)'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    title: {
        fontSize: '24px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        letterSpacing: '-0.5px',
        margin: 0
    },
    searchInput: {
        width: '100%',
        padding: '14px 16px',
        fontSize: '15px',
        border: '2px solid var(--border-color)',
        borderRadius: '12px',
        background: 'var(--bg-secondary)',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s',
        color: 'var(--text-primary)'
    },
    productsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: '16px',
        overflowY: 'auto',
        paddingRight: '8px'
    },
    productCard: {
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        padding: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: '8px',
        background: 'var(--bg-primary)'
    },
    productImage: {
        width: '60px',
        height: '60px',
        borderRadius: '8px',
        objectFit: 'cover',
        background: 'var(--bg-secondary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px'
    },
    productName: {
        fontSize: '13px',
        fontWeight: '600',
        color: 'var(--text-secondary)',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        height: '36px'
    },
    productPrice: {
        fontSize: '14px',
        fontWeight: '700',
        color: '#06C167'
    },
    productStock: {
        fontSize: '11px',
        color: 'var(--text-muted)',
        background: 'var(--bg-secondary)',
        padding: '2px 6px',
        borderRadius: '4px'
    },
    cartList: {
        flex: 1,
        overflowY: 'auto',
        marginBottom: '16px'
    },
    cartItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 0',
        borderBottom: '1px solid var(--border-color)',
        gap: '8px'
    },
    cartItemLeft: {
        flex: 1,
        minWidth: 0
    },
    cartItemName: {
        fontSize: '14px',
        fontWeight: '600',
        color: 'var(--text-primary)',
        marginBottom: '4px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    cartItemPrice: {
        fontSize: '13px',
        color: 'var(--text-muted)'
    },
    cartItemControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    qtyBtn: {
        width: '28px',
        height: '28px',
        borderRadius: '6px',
        border: '1px solid var(--border-color)',
        background: 'var(--bg-primary)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        color: 'var(--text-secondary)'
    },
    deleteBtn: {
        width: '28px',
        height: '28px',
        borderRadius: '6px',
        border: 'none',
        background: '#fef2f2',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        color: '#dc2626',
        transition: 'background 0.2s'
    },
    cartItemTotal: {
        fontSize: '14px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        width: '70px',
        textAlign: 'right'
    },
    summary: {
        borderTop: '2px dashed var(--border-color)',
        paddingTop: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '14px',
        color: 'var(--text-secondary)'
    },
    totalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '22px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        marginTop: '4px'
    },
    paymentMethods: {
        display: 'flex',
        gap: '12px',
        marginTop: '16px'
    },
    paymentBtn: {
        flex: 1,
        padding: '12px',
        border: '2px solid var(--border-color)',
        borderRadius: '12px',
        background: 'var(--bg-primary)',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        color: 'var(--text-muted)',
        transition: 'all 0.2s'
    },
    activePaymentBtn: {
        borderColor: '#06C167',
        background: '#e0e7ff',
        color: '#4338ca'
    },
    checkoutBtn: {
        width: '100%',
        padding: '16px',
        background: 'linear-gradient(135deg, #10b981, #059669)',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        fontSize: '16px',
        fontWeight: '700',
        cursor: 'pointer',
        marginTop: '16px',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
        transition: 'opacity 0.2s'
    },
    discountRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '8px'
    },
    discountInput: {
        width: '80px',
        padding: '8px 12px',
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        textAlign: 'center',
        background: 'var(--bg-secondary)',
        color: 'var(--text-primary)'
    },
    // Receipt Modal styles
    receiptOverlay: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100,
        backdropFilter: 'blur(4px)'
    },
    receiptContent: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        width: '360px',
        maxHeight: '90vh',
        overflowY: 'auto',
        color: '#0f172a'
    }
};

export default function StoreRetailSale() {
    const { userData, currentUser } = useAuth();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [cart, setCart] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [discountType, setDiscountType] = useState('percent'); // 'percent' or 'amount'
    const [discountValue, setDiscountValue] = useState(0);
    const [showReceipt, setShowReceipt] = useState(null);
    const searchRef = useRef(null);

    useEffect(() => {
        if (!userData || userData.role !== 'store' || !userData.companyId) return;

        const companyId = userData.companyId;
        const storeId = currentUser.uid;

        const unsub = onSnapshot(collection(db, 'companies', companyId, 'stores', storeId, 'inventory'), (snap) => {
            setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => unsub();
    }, [userData, currentUser]);

    const displayedProducts = useMemo(() => {
        if (!searchTerm) return products;
        return products.filter(p => 
            (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase())) || 
            (p.barcode && p.barcode.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [products, searchTerm]);

    const addToCart = (product) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                if (existing.qty + 1 > (product.quantity || 0)) {
                    alert('Stok yetersiz!');
                    return prev;
                }
                return prev.map(item => item.id === product.id ? { ...item, qty: item.qty + 1 } : item);
            }
            if ((product.quantity || 0) < 1) {
                alert('Stokta yok!');
                return prev;
            }
            return [...prev, { ...product, qty: 1 }];
        });
        setSearchTerm('');
        searchRef.current?.focus();
    };

    const updateQty = (id, delta) => {
        setCart(prev => {
            return prev.map(item => {
                if (item.id === id) {
                    const newQty = item.qty + delta;
                    if (newQty < 1) return null;
                    
                    const productInStock = products.find(p => p.id === id);
                    if (productInStock && newQty > (productInStock.quantity || 0)) {
                        alert('Stok yetersiz!');
                        return item;
                    }
                    return { ...item, qty: newQty };
                }
                return item;
            }).filter(Boolean);
        });
    };

    const removeFromCart = (id) => {
        setCart(prev => prev.filter(item => item.id !== id));
    };

    const clearCart = () => {
        setCart([]);
        setDiscountValue(0);
    };

    const totals = useMemo(() => {
        const subTotal = cart.reduce((sum, item) => sum + (item.salePrice || 0) * item.qty, 0);
        let discount = 0;
        if (discountType === 'percent') {
            discount = subTotal * (Number(discountValue) || 0) / 100;
        } else {
            discount = Number(discountValue) || 0;
        }
        discount = Math.min(discount, subTotal); // Can't exceed subtotal
        return {
            subTotal,
            discount,
            total: subTotal - discount
        };
    }, [cart, discountType, discountValue]);

    const handleCheckout = async () => {
        if (cart.length === 0) return;
        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            const companyId = userData.companyId;
            const storeId = currentUser.uid;

            const batch = writeBatch(db);

            const receiptNumber = 'R' + Date.now().toString().slice(-6) + Math.floor(Math.random() * 1000).toString().padStart(3, '0');

            const saleRef = doc(collection(db, 'companies', companyId, 'retailSales'));
            const saleData = {
                storeId,
                storeName: userData.storeName,
                items: cart.map(item => ({
                    productId: item.id,
                    productName: item.name,
                    quantity: item.qty,
                    price: item.salePrice || 0,
                    total: (item.salePrice || 0) * item.qty
                })),
                subTotal: totals.subTotal,
                discount: totals.discount,
                discountType: totals.discount > 0 ? discountType : null,
                discountValue: totals.discount > 0 ? Number(discountValue) : 0,
                total: totals.total,
                paymentMethod,
                saleDate: serverTimestamp(),
                soldBy: { uid: currentUser.uid, email: currentUser.email },
                receiptNumber,
                status: 'completed'
            };
            batch.set(saleRef, saleData);

            cart.forEach(item => {
                const invRef = doc(db, 'companies', companyId, 'stores', storeId, 'inventory', item.id);
                batch.update(invRef, {
                    quantity: increment(-item.qty)
                });
            });

            await batch.commit();

            // Show receipt modal
            setShowReceipt({
                ...saleData,
                receiptNumber,
                saleDate: new Date()
            });
            setCart([]);
            setDiscountValue(0);
            
        } catch (error) {
            console.error("Satış işlemi sırasında hata:", error);
            alert("Satış tamamlanamadı. Lütfen tekrar deneyin.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePrintReceipt = () => {
        window.print();
    };

    return (
        <div style={styles.container}>
            {/* Left Panel - Products */}
            <div style={styles.leftPanel}>
                <div style={styles.header}>
                    <h1 style={styles.title}>Perakende Satış</h1>
                </div>

                <div style={{ position: 'relative' }}>
                    <input 
                        ref={searchRef}
                        style={styles.searchInput}
                        type="text"
                        placeholder="Barkod okutun veya ürün adı arayın..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                    />
                </div>

                <div style={styles.productsGrid}>
                    {displayedProducts.map(product => (
                        <div 
                            key={product.id} 
                            style={{
                                ...styles.productCard,
                                opacity: (product.quantity || 0) === 0 ? 0.5 : 1
                            }}
                            onClick={() => addToCart(product)}
                            onMouseOver={(e) => { if (product.quantity > 0) e.currentTarget.style.borderColor = '#06C167'; }}
                            onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
                        >
                            {product.imageUrl ? (
                                <img src={product.imageUrl} alt={product.name} style={styles.productImage} />
                            ) : (
                                <div style={styles.productImage}>📦</div>
                            )}
                            <div style={styles.productName}>{product.name}</div>
                            <div style={styles.productPrice}>₺{(product.salePrice || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</div>
                            <div style={styles.productStock}>
                                {(product.quantity || 0) === 0 ? '❌ Tükendi' : `Stok: ${product.quantity}`}
                            </div>
                        </div>
                    ))}
                    {displayedProducts.length === 0 && (
                        <div style={{ padding: '20px', color: 'var(--text-muted)', gridColumn: '1 / -1' }}>
                            Ürün bulunamadı.
                        </div>
                    )}
                </div>
            </div>

            {/* Right Panel - Cart */}
            <div style={styles.rightPanel}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid var(--border-color)' }}>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
                        Sepet ({cart.reduce((s, i) => s + i.qty, 0)})
                    </h2>
                    {cart.length > 0 && (
                        <button onClick={clearCart} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                            Sepeti Temizle
                        </button>
                    )}
                </div>

                <div style={styles.cartList}>
                    {cart.map(item => (
                        <div key={item.id} style={styles.cartItem}>
                            <div style={styles.cartItemLeft}>
                                <div style={styles.cartItemName}>{item.name}</div>
                                <div style={styles.cartItemPrice}>₺{(item.salePrice || 0).toFixed(2)} / {item.unit || 'Adet'}</div>
                            </div>
                            <div style={styles.cartItemControls}>
                                <button style={styles.qtyBtn} onClick={() => updateQty(item.id, -1)}>-</button>
                                <span style={{ fontWeight: '600', width: '24px', textAlign: 'center', color: 'var(--text-primary)' }}>{item.qty}</span>
                                <button style={styles.qtyBtn} onClick={() => updateQty(item.id, 1)}>+</button>
                                <div style={styles.cartItemTotal}>
                                    ₺{((item.salePrice || 0) * item.qty).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                </div>
                                <button style={styles.deleteBtn} onClick={() => removeFromCart(item.id)} title="Sil">
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))}
                    {cart.length === 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🛒</div>
                            <p>Sepetiniz boş.</p>
                            <p style={{ fontSize: '13px' }}>Ürünlere tıklayarak sepete ekleyin</p>
                        </div>
                    )}
                </div>

                <div style={styles.summary}>
                    <div style={styles.summaryRow}>
                        <span>Alt Toplam</span>
                        <span style={{ fontWeight: '600' }}>₺{totals.subTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                    </div>

                    {/* Discount Row */}
                    <div style={{ ...styles.summaryRow, alignItems: 'center' }}>
                        <span>İndirim</span>
                        <div style={styles.discountRow}>
                            <select
                                value={discountType}
                                onChange={(e) => setDiscountType(e.target.value)}
                                style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '12px', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                            >
                                <option value="percent">%</option>
                                <option value="amount">₺</option>
                            </select>
                            <input
                                style={styles.discountInput}
                                type="number"
                                min="0"
                                step="0.01"
                                value={discountValue}
                                onChange={(e) => setDiscountValue(e.target.value)}
                                placeholder="0"
                            />
                        </div>
                    </div>

                    {totals.discount > 0 && (
                        <div style={{ ...styles.summaryRow, color: '#dc2626' }}>
                            <span>İndirim Tutarı</span>
                            <span style={{ fontWeight: '600' }}>-₺{totals.discount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                        </div>
                    )}

                    <div style={styles.totalRow}>
                        <span>Genel Toplam</span>
                        <span>₺{totals.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                    </div>

                    <div style={styles.paymentMethods}>
                        <button 
                            style={{ ...styles.paymentBtn, ...(paymentMethod === 'cash' ? styles.activePaymentBtn : {}) }}
                            onClick={() => setPaymentMethod('cash')}
                        >
                            💵 Nakit
                        </button>
                        <button 
                            style={{ ...styles.paymentBtn, ...(paymentMethod === 'card' ? styles.activePaymentBtn : {}) }}
                            onClick={() => setPaymentMethod('card')}
                        >
                            💳 Kredi Kartı
                        </button>
                    </div>

                    <button 
                        style={{ ...styles.checkoutBtn, opacity: (cart.length === 0 || isSubmitting) ? 0.5 : 1 }}
                        disabled={cart.length === 0 || isSubmitting}
                        onClick={handleCheckout}
                    >
                        {isSubmitting ? 'İşleniyor...' : 'Satışı Tamamla'}
                    </button>
                </div>
            </div>

            {/* Receipt Modal */}
            {showReceipt && (
                <div style={styles.receiptOverlay} onClick={() => setShowReceipt(null)}>
                    <div style={styles.receiptContent} onClick={e => e.stopPropagation()} className="receipt-print">
                        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                            <div style={{ fontSize: '24px', marginBottom: '8px' }}>✅</div>
                            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>Satış Tamamlandı</h2>
                            <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>Fiş No: #{showReceipt.receiptNumber}</p>
                        </div>

                        <div style={{ borderTop: '1px dashed #e2e8f0', borderBottom: '1px dashed #e2e8f0', padding: '16px 0', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', color: '#64748b' }}>
                                <span>Mağaza:</span>
                                <strong style={{color: '#0f172a'}}>{showReceipt.storeName}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', color: '#64748b' }}>
                                <span>Tarih:</span>
                                <strong style={{color: '#0f172a'}}>{showReceipt.saleDate.toLocaleString('tr-TR')}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b' }}>
                                <span>Ödeme:</span>
                                <strong style={{color: '#0f172a'}}>{showReceipt.paymentMethod === 'cash' ? 'Nakit' : 'Kredi Kartı'}</strong>
                            </div>
                        </div>

                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', fontSize: '12px', color: '#64748b' }}>Ürün</th>
                                    <th style={{ textAlign: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', fontSize: '12px', color: '#64748b' }}>Adet</th>
                                    <th style={{ textAlign: 'right', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', fontSize: '12px', color: '#64748b' }}>Tutar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(showReceipt.items || []).map((item, idx) => (
                                    <tr key={idx}>
                                        <td style={{ padding: '8px 0', fontSize: '13px' }}>{item.productName}</td>
                                        <td style={{ padding: '8px 0', fontSize: '13px', textAlign: 'center' }}>{item.quantity}</td>
                                        <td style={{ padding: '8px 0', fontSize: '13px', textAlign: 'right', fontWeight: '500' }}>₺{(item.total || 0).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px', color: '#64748b' }}>
                                <span>Alt Toplam:</span>
                                <span>₺{(showReceipt.subTotal || 0).toFixed(2)}</span>
                            </div>
                            {showReceipt.discount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px', color: '#dc2626' }}>
                                    <span>İndirim:</span>
                                    <span>-₺{(showReceipt.discount || 0).toFixed(2)}</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: '700', marginTop: '8px', color: '#0f172a' }}>
                                <span>Toplam:</span>
                                <span>₺{(showReceipt.total || 0).toFixed(2)}</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                            <button
                                onClick={handlePrintReceipt}
                                style={{ flex: 1, padding: '12px', background: '#06C167', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}
                            >
                                🖨️ Yazdır
                            </button>
                            <button
                                onClick={() => setShowReceipt(null)}
                                style={{ flex: 1, padding: '12px', background: '#f1f5f9', color: '#334155', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}
                            >
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
